// KeyValueButtons.tsx
import {
  Box,
  Grid,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { memo, useCallback, useEffect } from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  OnDragEndResponder,
} from "react-beautiful-dnd";
import { Button, useFormContext } from "@4uhub/lib4uhub";
import { useFieldArray, Controller, FieldPath } from "react-hook-form";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { TProtocolDefaultForm } from "./ProtocolDefaultSchema";

const translationPath = "page.settings.crm.protocolsDefault.";

interface IProps {
  maxItems?: number;
  disabled?: boolean;
}

export const KeyValueButtons = memo(
  ({ maxItems = 3, disabled = false }: IProps) => {
    const { t } = useTranslation();
    const { control, getValues, setValue } =
      useFormContext<TProtocolDefaultForm>();

    useEffect(() => {
      const current = getValues("ticketDefaultDocButtons");
      if (!Array.isArray(current)) {
        setValue("ticketDefaultDocButtons", [], {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
    }, [getValues, setValue]);

    const { fields, append, remove, move } = useFieldArray<
      TProtocolDefaultForm,
      "ticketDefaultDocButtons",
      "keyId"
    >({
      control,
      name: "ticketDefaultDocButtons",
      keyName: "keyId",
    });

    const canAdd = fields.length < maxItems;

    const addItem = useCallback(() => {
      if (!canAdd) return;
      append({ label: "", value: "" } as any);
    }, [append, canAdd]);

    const onDragEnd: OnDragEndResponder = useCallback(
      (e) => {
        if (!e.destination) return;
        if (e.source.index === e.destination.index) return;
        move(e.source.index, e.destination.index);
      },
      [move]
    );

    if (fields.length === 0 && disabled) return null;

    return (
      <Box
        sx={(t) => ({
          p: 2,
          border: 1,
          borderColor: t.palette.divider,
          borderRadius: 1,
        })}
      >
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" fontWeight="bold">
            {t(translationPath + "button_title") || "Buttons"}
          </Typography>

          <Box sx={{ flex: 1 }} />

          <Button size="small" onClick={addItem} disabled={!canAdd || disabled}>
            <AddIcon fontSize="small" sx={{ mr: 0.5 }} />
            {t(translationPath + "add") || "Adicionar"}
          </Button>

          <Tooltip
            title={
              t(translationPath + "counter_hint") ||
              "Quantidade atual / máximo permitido"
            }
          >
            <Typography variant="caption" color="text.secondary">
              {fields.length}/{maxItems}
            </Typography>
          </Tooltip>
        </Box>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable
            droppableId="ticketDefaultDocButtonslist"
            direction="vertical"
          >
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {fields.map((field, index) => (
                  <Draggable
                    key={field.keyId}
                    draggableId={String(field.keyId)}
                    index={index}
                    isDragDisabled={fields.length < 2 || disabled}
                  >
                    {(dragProvided, snapshot) => (
                      <Box
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                        sx={(t) => ({
                          p: 1.5,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          borderRadius: 1,
                          boxShadow: snapshot.isDragging ? 1 : "unset",
                          backgroundColor: t.palette.background.default,
                          "&:hover": {
                            backgroundColor:
                              fields.length >= 2
                                ? t.palette.grey[
                                    t.palette.mode === "light" ? 100 : 900
                                  ]
                                : undefined,
                          },
                        })}
                      >
                        {fields.length >= 2 && !disabled && (
                          <DragIndicatorIcon fontSize="small" color="inherit" />
                        )}

                        <Box
                          sx={(t) => ({
                            p: 2,
                            width: "100%",
                            backgroundColor:
                              t.palette.grey[
                                t.palette.mode === "light" ? 200 : 800
                              ],
                            border: 1,
                            borderColor: t.palette.divider,
                            borderRadius: 1,
                          })}
                        >
                          <Grid container spacing={1}>
                            <Grid item xs={12} sm={maxItems === 1 ? 6 : 12}>
                              <Controller<TProtocolDefaultForm>
                                name={`ticketDefaultDocButtons.${index}.label`}
                                control={control}
                                defaultValue={(field as any).label ?? ""}
                                render={({ field, fieldState }) => (
                                  <TextField
                                    {...field}
                                    label={
                                      t(translationPath + "label") || "Label"
                                    }
                                    fullWidth
                                    size="small"
                                    disabled={disabled}
                                    error={!!fieldState.error}
                                    helperText={t(
                                      fieldState.error?.message || " "
                                    )}
                                    sx={(t) => ({
                                      background: `linear-gradient(${t.palette.background.default} 40px, transparent 40px)`,
                                      borderRadius: 1,
                                    })}
                                    inputProps={{ maxLength: 20 }}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          <Typography
                                            variant="body2"
                                            sx={(t) => ({
                                              color:
                                                t.palette.grey[
                                                  t.palette.mode === "light"
                                                    ? 400
                                                    : 600
                                                ],
                                            })}
                                          >
                                            {field.value
                                              ? field.value.toString().length
                                              : 0}
                                            /{20}
                                          </Typography>
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                )}
                              />
                            </Grid>
                            {maxItems === 1 && (
                              <Grid item xs={12} sm={6}>
                                <Controller<TProtocolDefaultForm>
                                  name={`ticketDefaultDocButtons.${index}.value`}
                                  control={control}
                                  defaultValue={(field as any).value ?? ""}
                                  render={({ field, fieldState }) => (
                                    <TextField
                                      {...field}
                                      label={
                                        maxItems === 1
                                          ? t(translationPath + "link") ||
                                            "Link"
                                          : t(translationPath + "value") ||
                                            "Value"
                                      }
                                      fullWidth
                                      size="small"
                                      disabled={disabled}
                                      error={!!fieldState.error}
                                      helperText={t(
                                        fieldState.error?.message || " "
                                      )}
                                      sx={(t) => ({
                                        background: `linear-gradient(${t.palette.background.default} 40px, transparent 40px)`,
                                        borderRadius: 1,
                                      })}
                                    />
                                  )}
                                />
                              </Grid>
                            )}
                          </Grid>
                        </Box>

                        {!disabled && (
                          <Button
                            size="small"
                            color="inherit"
                            sx={{ minWidth: 0 }}
                            onClick={() => remove(index)}
                          >
                            <CloseIcon fontSize="small" />
                          </Button>
                        )}
                      </Box>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
    );
  }
);

export default KeyValueButtons;
